import { useMemo, useState } from 'react';
import {
  fieldsForLevel,
  formatPace,
  paceSecPerKm,
  trackingCopy,
  zoneBoundsFor,
  type Physiology,
  type TrackingLevel,
  type ZoneModel,
} from '../../lib/cardioTracking';
import { calorieSourceLabel, estimateSmartCardioCalories } from '../../lib/cardioCalories';
import { metersFromDistanceInput } from '../../lib/cardioTracking';
import { emptyLap, emptySegment, formatIntervalLine, type DraftSegment } from '../../lib/cardioDraft';
import { WORKOUT_ACTIVITY_TYPES } from '../../lib/workoutActivity';
import type { UserProfile } from '../../types';

const field =
  'w-full rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-[var(--text-primary)] outline-none';
const label = 'text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]';

const SPORT_SEGS = WORKOUT_ACTIVITY_TYPES.filter(
  (a) => a.cardio && a.id !== 'multisport' && a.id !== 'yoga' && a.id !== 'pilates',
);

export type EquipmentRow = {
  id: string;
  kind: string;
  name: string;
  distanceMeters: number;
};

export function CardioSessionPanel({
  activityType,
  level,
  onOpenTracking,
  durationMinutesStr,
  onDurationChange,
  segments,
  onSegmentsChange,
  imperial,
  profile,
  physiology,
  onPhysiologyChange,
  equipment,
  equipmentId,
  onEquipmentId,
  onAddEquipment,
  caloriesOverride,
  onCaloriesOverride,
  perceivedEffort,
  onPerceivedEffort,
}: {
  activityType: string;
  level: TrackingLevel;
  onOpenTracking: () => void;
  durationMinutesStr: string;
  onDurationChange: (v: string) => void;
  segments: DraftSegment[];
  onSegmentsChange: (next: DraftSegment[]) => void;
  imperial: boolean;
  profile: UserProfile | null;
  physiology: Physiology | null;
  onPhysiologyChange: (p: Physiology) => void;
  equipment: EquipmentRow[];
  equipmentId: string;
  onEquipmentId: (id: string) => void;
  onAddEquipment: (kind: string, name: string) => void;
  caloriesOverride: string;
  onCaloriesOverride: (v: string) => void;
  perceivedEffort: string;
  onPerceivedEffort: (v: string) => void;
}) {
  const fields = fieldsForLevel(activityType, level);
  const distUnit = imperial ? 'mi' : 'km';
  const primary = segments[0] ?? emptySegment(activityType);
  const durationMin = Number(durationMinutesStr);
  const durationSeconds = Number.isFinite(durationMin) && durationMin > 0 ? durationMin * 60 : null;
  const distanceM = metersFromDistanceInput(primary.distance, imperial);
  const pace = formatPace(paceSecPerKm(distanceM, durationSeconds), imperial);
  const kcal = estimateSmartCardioCalories({
    activityType,
    durationMinutes: Number.isFinite(durationMin) ? durationMin : null,
    distanceMeters: distanceM,
    elevGainM: Number(primary.elevGain) || null,
    avgHr: Number(primary.avgHr) || null,
    bodyWeightKg: profile?.weightKg,
    age: profile?.age,
    sex: profile?.sex,
    caloriesOverride: Number(caloriesOverride) || null,
  });
  const copy = trackingCopy(activityType);
  const levelLabel = copy.options.find((o) => o.level === level)?.label ?? level;
  const [gearName, setGearName] = useState('');
  const [physOpen, setPhysOpen] = useState(false);

  function patch(i: number, p: Partial<DraftSegment>) {
    onSegmentsChange(segments.map((s, j) => (j === i ? { ...s, ...p } : s)));
  }

  const isMulti = activityType === 'multisport' || fields.has('segments');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between rounded-2xl border border-white/8 bg-white/[0.03] px-3 py-2">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
            Tracking · this activity only
          </div>
          <div className="text-sm font-semibold text-[var(--text-primary)]">{levelLabel}</div>
        </div>
        <button type="button" onClick={onOpenTracking} className="text-xs font-bold text-sky-300">
          {fields.has('distance') ? 'Change' : 'Add distance'}
        </button>
      </div>

      <label className="block">
        <span className={label}>Duration (min)</span>
        <input
          inputMode="numeric"
          value={durationMinutesStr}
          onChange={(e) => onDurationChange(e.target.value)}
          placeholder="30"
          className={`${field} mt-1`}
        />
      </label>

      <label className="block">
        <span className={label}>Session RPE (optional)</span>
        <input
          inputMode="decimal"
          value={perceivedEffort}
          onChange={(e) => onPerceivedEffort(e.target.value)}
          placeholder="6"
          className={`${field} mt-1`}
        />
      </label>

      {isMulti ? (
        <MultisportEditor
          segments={segments}
          onChange={onSegmentsChange}
          imperial={imperial}
          fields={fields}
          level={level}
        />
      ) : (
        <SegmentFields
          d={primary}
          onChange={(p) => patch(0, p)}
          imperial={imperial}
          fields={fields}
          pace={pace}
          distUnit={distUnit}
        />
      )}

      <div className="rounded-2xl border border-white/8 bg-white/[0.03] px-3 py-3">
        <div className="text-sm font-semibold text-[var(--text-primary)]">~{kcal.kcal} kcal</div>
        <div className="text-xs text-[var(--text-muted)]">{calorieSourceLabel(kcal.source)}</div>
        <input
          inputMode="numeric"
          value={caloriesOverride}
          onChange={(e) => onCaloriesOverride(e.target.value)}
          placeholder="Override kcal"
          className={`${field} mt-2`}
        />
      </div>

      {fields.has('zones') ? (
        <button type="button" onClick={() => setPhysOpen((o) => !o)} className="text-xs font-bold text-sky-300">
          {physOpen ? 'Hide' : 'Heart rate zones / FTP'}
        </button>
      ) : null}
      {physOpen && fields.has('zones') ? (
        <PhysiologyFields value={physiology} onChange={onPhysiologyChange} />
      ) : null}

      {fields.has('equipment') ? (
        <div className="space-y-2 rounded-2xl border border-white/8 bg-white/[0.03] p-3">
          <span className={label}>Equipment</span>
          <select value={equipmentId} onChange={(e) => onEquipmentId(e.target.value)} className={field}>
            <option value="">None</option>
            {equipment.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name} · {imperial ? `${(e.distanceMeters / 1609.34).toFixed(0)} mi` : `${(e.distanceMeters / 1000).toFixed(0)} km`}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <input
              value={gearName}
              onChange={(e) => setGearName(e.target.value)}
              placeholder="Add shoes or bike"
              className={field}
            />
            <button
              type="button"
              onClick={() => {
                if (!gearName.trim()) return;
                onAddEquipment(activityType.includes('ride') || activityType.includes('bike') ? 'bike' : 'shoes', gearName.trim());
                setGearName('');
              }}
              className="shrink-0 rounded-xl border border-white/15 px-3 text-xs font-bold"
            >
              Add
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SegmentFields({
  d,
  onChange,
  imperial,
  fields,
  pace,
  distUnit,
}: {
  d: DraftSegment;
  onChange: (p: Partial<DraftSegment>) => void;
  imperial: boolean;
  fields: Set<string>;
  pace: string;
  distUnit: string;
}) {
  if (d.isTransition) {
    return (
      <label className="block">
        <span className={label}>Transition (min)</span>
        <input
          inputMode="numeric"
          value={d.durationMinutes}
          onChange={(e) => onChange({ durationMinutes: e.target.value })}
          className={`${field} mt-1`}
        />
      </label>
    );
  }
  return (
    <div className="space-y-3 rounded-2xl border border-white/8 bg-[#121a2e] p-3">
      {fields.has('distance') ? (
        <div>
          <label className="block">
            <span className={label}>Distance ({distUnit})</span>
            <input
              inputMode="decimal"
              value={d.distance}
              onChange={(e) => onChange({ distance: e.target.value })}
              placeholder={imperial ? '3.1' : '5'}
              className={`${field} mt-1`}
            />
          </label>
          <p className="mt-1 text-xs text-[var(--text-muted)]">Pace {pace}</p>
        </div>
      ) : null}
      {fields.has('hr') ? (
        <div className="grid grid-cols-2 gap-2">
          <label>
            <span className={label}>Avg HR</span>
            <input inputMode="numeric" value={d.avgHr} onChange={(e) => onChange({ avgHr: e.target.value })} className={`${field} mt-1`} />
          </label>
          <label>
            <span className={label}>Max HR</span>
            <input inputMode="numeric" value={d.maxHr} onChange={(e) => onChange({ maxHr: e.target.value })} className={`${field} mt-1`} />
          </label>
        </div>
      ) : null}
      {fields.has('elevation') ? (
        <div className="grid grid-cols-2 gap-2">
          <label>
            <span className={label}>Elev gain (m)</span>
            <input inputMode="decimal" value={d.elevGain} onChange={(e) => onChange({ elevGain: e.target.value })} className={`${field} mt-1`} />
          </label>
          <label>
            <span className={label}>Elev loss (m)</span>
            <input inputMode="decimal" value={d.elevLoss} onChange={(e) => onChange({ elevLoss: e.target.value })} className={`${field} mt-1`} />
          </label>
        </div>
      ) : null}
      {fields.has('cadence') ? (
        <label>
          <span className={label}>Cadence</span>
          <input inputMode="numeric" value={d.cadence} onChange={(e) => onChange({ cadence: e.target.value })} className={`${field} mt-1`} />
        </label>
      ) : null}
      {fields.has('zones') ? (
        <div>
          <span className={label}>Zone minutes</span>
          <div className="mt-1 grid grid-cols-5 gap-1">
            {(['z1', 'z2', 'z3', 'z4', 'z5'] as const).map((k, i) => (
              <input
                key={k}
                inputMode="numeric"
                value={d[k]}
                onChange={(e) => onChange({ [k]: e.target.value })}
                placeholder={`Z${i + 1}`}
                className={`${field} px-1 text-center`}
              />
            ))}
          </div>
          <select
            value={d.zoneModel}
            onChange={(e) => onChange({ zoneModel: e.target.value as ZoneModel })}
            className={`${field} mt-2`}
          >
            <option value="max_hr">% max HR</option>
            <option value="hrr">Heart rate reserve</option>
            <option value="lthr">Lactate threshold</option>
          </select>
        </div>
      ) : null}
      {fields.has('intervals') ? <IntervalFields d={d} onChange={onChange} imperial={imperial} distUnit={distUnit} /> : null}
      {fields.has('power') ? (
        <div className="grid grid-cols-2 gap-2">
          <label>
            <span className={label}>Avg W</span>
            <input inputMode="numeric" value={d.avgPower} onChange={(e) => onChange({ avgPower: e.target.value })} className={`${field} mt-1`} />
          </label>
          <label>
            <span className={label}>NP / TSS</span>
            <div className="mt-1 flex gap-1">
              <input inputMode="numeric" value={d.np} onChange={(e) => onChange({ np: e.target.value })} placeholder="NP" className={field} />
              <input inputMode="numeric" value={d.tss} onChange={(e) => onChange({ tss: e.target.value })} placeholder="TSS" className={field} />
            </div>
          </label>
        </div>
      ) : null}
      {fields.has('swim') ? (
        <div className="grid grid-cols-3 gap-2">
          <label>
            <span className={label}>Pool m</span>
            <input inputMode="numeric" value={d.poolLength} onChange={(e) => onChange({ poolLength: e.target.value })} className={`${field} mt-1`} />
          </label>
          <label>
            <span className={label}>Stroke</span>
            <input value={d.stroke} onChange={(e) => onChange({ stroke: e.target.value })} className={`${field} mt-1`} />
          </label>
          <label>
            <span className={label}>SWOLF</span>
            <input inputMode="decimal" value={d.swolf} onChange={(e) => onChange({ swolf: e.target.value })} className={`${field} mt-1`} />
          </label>
        </div>
      ) : null}
    </div>
  );
}

function IntervalFields({
  d,
  onChange,
  imperial,
  distUnit,
}: {
  d: DraftSegment;
  onChange: (p: Partial<DraftSegment>) => void;
  imperial: boolean;
  distUnit: string;
}) {
  const preview = useMemo(
    () =>
      formatIntervalLine(
        {
          reps: Number(d.intervalReps) || 0,
          workDistanceM: metersFromDistanceInput(d.workDistance, imperial),
          workSeconds: d.workTime ? Number(d.workTime.split(':')[0]) * 60 + Number(d.workTime.split(':')[1] || 0) : null,
          targetPaceSecPerKm: null,
          restSeconds: Number(d.restSeconds) || null,
        },
        imperial,
      ),
    [d.intervalReps, d.workDistance, d.workTime, d.restSeconds, imperial],
  );

  function seedSplits() {
    const reps = Math.min(20, Math.max(1, Math.round(Number(d.intervalReps) || 0)));
    const laps = [];
    for (let i = 0; i < reps; i++) {
      laps.push(emptyLap('work'));
      if (d.restSeconds) laps.push(emptyLap('rest'));
    }
    onChange({ showSplits: true, laps });
  }

  return (
    <div className="space-y-2">
      <span className={label}>Intervals</span>
      {preview ? <p className="text-xs text-sky-200">{preview}</p> : null}
      <div className="grid grid-cols-2 gap-2">
        <input inputMode="numeric" value={d.intervalReps} onChange={(e) => onChange({ intervalReps: e.target.value })} placeholder="6" className={field} />
        <input
          inputMode="decimal"
          value={d.workDistance}
          onChange={(e) => onChange({ workDistance: e.target.value })}
          placeholder={`Work ${distUnit === 'mi' ? '0.5' : '0.8'}`}
          className={field}
        />
        <input value={d.workTime} onChange={(e) => onChange({ workTime: e.target.value })} placeholder="Work 3:20" className={field} />
        <input value={d.targetPace} onChange={(e) => onChange({ targetPace: e.target.value })} placeholder="Pace 3:20" className={field} />
        <input inputMode="numeric" value={d.restSeconds} onChange={(e) => onChange({ restSeconds: e.target.value })} placeholder="Rest sec" className={field} />
      </div>
      <button type="button" onClick={seedSplits} className="text-xs font-bold text-sky-300">
        {d.showSplits ? 'Edit actual splits' : 'Add actual splits (optional)'}
      </button>
      {d.showSplits
        ? d.laps.map((lap, i) => (
            <div key={lap.key} className="grid grid-cols-4 gap-1">
              <select
                value={lap.kind}
                onChange={(e) =>
                  onChange({
                    laps: d.laps.map((x, j) => (j === i ? { ...x, kind: e.target.value as DraftSegment['laps'][0]['kind'] } : x)),
                  })
                }
                className={field}
              >
                <option value="work">Work</option>
                <option value="rest">Rest</option>
                <option value="lap">Lap</option>
              </select>
              <input
                value={lap.duration}
                onChange={(e) =>
                  onChange({ laps: d.laps.map((x, j) => (j === i ? { ...x, duration: e.target.value } : x)) })
                }
                placeholder="3:18"
                className={field}
              />
              <input
                value={lap.distance}
                onChange={(e) =>
                  onChange({ laps: d.laps.map((x, j) => (j === i ? { ...x, distance: e.target.value } : x)) })
                }
                placeholder={distUnit}
                className={field}
              />
              <input
                value={lap.avgHr}
                onChange={(e) =>
                  onChange({ laps: d.laps.map((x, j) => (j === i ? { ...x, avgHr: e.target.value } : x)) })
                }
                placeholder="HR"
                className={field}
              />
            </div>
          ))
        : null}
    </div>
  );
}

function MultisportEditor({
  segments,
  onChange,
  imperial,
  fields,
  level,
}: {
  segments: DraftSegment[];
  onChange: (next: DraftSegment[]) => void;
  imperial: boolean;
  fields: Set<string>;
  level: TrackingLevel;
}) {
  const distUnit = imperial ? 'mi' : 'km';
  function patch(i: number, p: Partial<DraftSegment>) {
    onChange(segments.map((s, j) => (j === i ? { ...s, ...p } : s)));
  }
  return (
    <div className="space-y-3">
      {segments.map((seg, i) => (
        <div key={seg.key} className="space-y-2">
          <div className="flex items-center gap-2">
            {seg.isTransition ? (
              <span className="text-xs font-bold uppercase text-[var(--text-muted)]">Transition</span>
            ) : (
              <select
                value={seg.activityType}
                onChange={(e) => patch(i, { activityType: e.target.value })}
                className={field}
              >
                {SPORT_SEGS.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </select>
            )}
            {segments.length > 1 ? (
              <button
                type="button"
                onClick={() => onChange(segments.filter((_, j) => j !== i))}
                className="text-xs text-red-300"
              >
                Remove
              </button>
            ) : null}
          </div>
          <label>
            <span className={label}>Minutes</span>
            <input
              inputMode="numeric"
              value={seg.durationMinutes}
              onChange={(e) => patch(i, { durationMinutes: e.target.value })}
              className={`${field} mt-1`}
            />
          </label>
          <SegmentFields
            d={seg}
            onChange={(p) => patch(i, p)}
            imperial={imperial}
            fields={fields}
            pace={formatPace(
              paceSecPerKm(
                metersFromDistanceInput(seg.distance, imperial),
                Number(seg.durationMinutes) > 0 ? Number(seg.durationMinutes) * 60 : null,
              ),
              imperial,
            )}
            distUnit={distUnit}
          />
        </div>
      ))}
      {level !== 'quick' ? (
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onChange([...segments, emptySegment('outdoor_run')])}
            className="rounded-xl border border-white/15 px-3 py-2 text-xs font-bold"
          >
            + Sport
          </button>
          <button
            type="button"
            onClick={() => onChange([...segments, emptySegment('other_cardio', true)])}
            className="rounded-xl border border-white/15 px-3 py-2 text-xs font-bold"
          >
            + Transition
          </button>
        </div>
      ) : null}
    </div>
  );
}

function PhysiologyFields({
  value,
  onChange,
}: {
  value: Physiology | null;
  onChange: (p: Physiology) => void;
}) {
  const v: Physiology = value ?? {
    maxHr: null,
    restingHr: null,
    lthr: null,
    ftpWatts: null,
    thresholdPaceSecPerKm: null,
    zoneModel: 'max_hr',
  };
  const bounds = zoneBoundsFor(v.zoneModel, v);
  return (
    <div className="space-y-2 rounded-2xl border border-white/8 p-3">
      <p className="text-xs text-[var(--text-muted)]">Optional. Never required to save a workout.</p>
      <div className="grid grid-cols-2 gap-2">
        <input
          inputMode="numeric"
          placeholder="Max HR"
          value={v.maxHr ?? ''}
          onChange={(e) => onChange({ ...v, maxHr: Number(e.target.value) || null })}
          className={field}
        />
        <input
          inputMode="numeric"
          placeholder="Resting HR"
          value={v.restingHr ?? ''}
          onChange={(e) => onChange({ ...v, restingHr: Number(e.target.value) || null })}
          className={field}
        />
        <input
          inputMode="numeric"
          placeholder="LTHR"
          value={v.lthr ?? ''}
          onChange={(e) => onChange({ ...v, lthr: Number(e.target.value) || null })}
          className={field}
        />
        <input
          inputMode="numeric"
          placeholder="FTP watts"
          value={v.ftpWatts ?? ''}
          onChange={(e) => onChange({ ...v, ftpWatts: Number(e.target.value) || null })}
          className={field}
        />
      </div>
      <p className="text-[10px] text-[var(--text-muted)]">
        {bounds.map((b) => `${b.label} ${b.lo}–${b.hi}`).join(' · ')}
      </p>
    </div>
  );
}
